import React, { useState } from 'react';
import './Overview.css';
import { QRCodeSVG } from 'qrcode.react';
import { createPayment, revokeKey, getSubscriptionDevices, deleteSubscriptionDevice } from '../../api';

const formatDeviceDate = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
};

const Overview = ({
  hasSub,
  subsVM,
  anySelected,
  selectedCount,
  startRenew,
  startNew,
  deviceLimit,
  lteLimit,
  navTo,
}) => {
  const [expandedSubId, setExpandedSubId] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedTraffic, setSelectedTraffic] = useState('50');
  const [selectedPayment, setSelectedPayment] = useState('sbp');
  const [loadingPay, setLoadingPay] = useState(false);
  const [reissueModalOpen, setReissueModalOpen] = useState(false);
  const [reissueSubId, setReissueSubId] = useState(null);
  const [loadingReissue, setLoadingReissue] = useState(false);
  const [reissueSuccessOpen, setReissueSuccessOpen] = useState(false);

  const [devicesModalOpen, setDevicesModalOpen] = useState(false);
  const [devicesSubId, setDevicesSubId] = useState(null);
  const [devicesList, setDevicesList] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [loadingDeleteDevice, setLoadingDeleteDevice] = useState(false);

  const trafficPrices = {
    '50': 40,
    '100': 75,
    '150': 110,
    '300': 225,
    '600': 450,
    '1000': 690,
    '2000': 1375,   
    '3000': 2045,   
  };

  const packagePrices = {
    '30': {
      '50': 299,
      '100': 339,
      '150': 375,
      '300': 482,
      '600': 690,
      '1000': 959,
      '2000': 1639,   
      '3000': 2309,   
    },
    '90': {
      '150': 849,
      '300': 959,
      '600': 1169,
      '1000': 1440,
      '2000': 2119,   
      '3000': 2789,   
    },
    '180': {
      '300': 1599,
      '600': 1812,
      '1000': 2086,
      '2000': 2765,   
      '3000': 3434,   
    },
    '365': {
      '600': 2749,
      '1000': 3033,
      '2000': 3702,   
      '3000': 4369,   
    },
  };

  const getAvailableTraffic = (term) => {
    if (!term) return ['50', '100', '150', '300', '600', '1000', '2000', '3000']; 
    if (term === '30') return ['50', '100', '150', '300', '600', '1000', '2000', '3000']; 
    if (term === '90') return ['150', '300', '600', '1000', '2000', '3000']; 
    if (term === '180') return ['300', '600', '1000', '2000', '3000']; 
    if (term === '365') return ['600', '1000', '2000', '3000']; 
    return ['50', '100', '150', '300', '600', '1000', '2000', '3000']; 
  };

  const availableTraffic = getAvailableTraffic(selectedTerm);

  const trafficPrice = trafficPrices[selectedTraffic] || 0;

  const currentPrice = selectedTerm
    ? (packagePrices[selectedTerm]?.[selectedTraffic] ?? 0)
    : trafficPrice;

  const minTraffic = availableTraffic[0];
  const basePrice = selectedTerm
    ? (packagePrices[selectedTerm]?.[minTraffic] ?? 0)
    : 0;
  const priceAddition = currentPrice - basePrice;

  const handleTermSelect = (term) => {
    const newTerm = selectedTerm === term ? null : term;
    setSelectedTerm(newTerm);
    const available = getAvailableTraffic(newTerm);
    if (!available.includes(selectedTraffic)) {
      setSelectedTraffic(available[0]);
    }
  };

  const handleRenew = async (subId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Не авторизован. Пожалуйста, войдите в аккаунт.');
      return;
    }

    if (loadingPay) return;

    setLoadingPay(true);
    try {
      const daysMap = { '30': 30, '90': 90, '180': 180, '365': 365 };
      const days = daysMap[selectedTerm] || 0;

      const gbMap = { '50': 50, '100': 100, '150': 150, '300': 300, '600': 600, '1000': 1000, '2000': 2000, '3000': 3000 };
      const gb = gbMap[selectedTraffic] || 50;

      const amount = currentPrice;

      const site_uuid = [subId];

      const result = await createPayment(token, amount, days, selectedPayment, site_uuid, gb);

      if (result.payment_url) {
        window.location.href = result.payment_url;
      } else {
        throw new Error('Не получена ссылка на оплату');
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка создания платежа: ' + (err.message || 'Неизвестная ошибка'));
    } finally {
      setLoadingPay(false);
    }
  };

  const handleReissue = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Не авторизован. Пожалуйста, войдите в аккаунт.');
      return;
    }

    if (!reissueSubId || loadingReissue) return;

    setLoadingReissue(true);
    try {
      await revokeKey(token, reissueSubId);
      setReissueModalOpen(false);
      setReissueSubId(null);
      setReissueSuccessOpen(true);
    } catch (err) {
      console.error(err);
      alert('Ошибка перевыпуска: ' + (err.message || 'Неизвестная ошибка'));
    } finally {
      setLoadingReissue(false);
    }
  };

  const openDevicesModal = async (subId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Не авторизован. Пожалуйста, войдите в аккаунт.');
      return;
    }
    setDevicesSubId(subId);
    setDevicesModalOpen(true);
    setDevicesList([]);
    setLoadingDevices(true);
    try {
      const data = await getSubscriptionDevices(token, subId);
      setDevicesList(Array.isArray(data.devices) ? data.devices : []);
    } catch (err) {
      console.error(err);
      alert('Ошибка загрузки устройств: ' + (err.message || 'Неизвестная ошибка'));
      setDevicesModalOpen(false);
      setDevicesSubId(null);
    } finally {
      setLoadingDevices(false);
    }
  };

  const closeDevicesModal = () => {
    if (loadingDeleteDevice) return;
    setDevicesModalOpen(false);
    setDevicesSubId(null);
    setDevicesList([]);
    setDeleteConfirmOpen(false);
    setDeviceToDelete(null);
  };

  const requestDeleteDevice = (device) => {
    setDeviceToDelete(device);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteDevice = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Не авторизован. Пожалуйста, войдите в аккаунт.');
      return;
    }
    if (!devicesSubId || !deviceToDelete?.hwid || loadingDeleteDevice) return;

    setLoadingDeleteDevice(true);
    try {
      await deleteSubscriptionDevice(token, devicesSubId, deviceToDelete.hwid);
      setDevicesList((prev) => prev.filter((d) => d.hwid !== deviceToDelete.hwid));
      setDeleteConfirmOpen(false);
      setDeviceToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Ошибка удаления устройства: ' + (err.message || 'Неизвестная ошибка'));
    } finally {
      setLoadingDeleteDevice(false);
    }
  };

  if (!hasSub) {
    return (
      <div className="no-sub-card">
        <div className="no-sub-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7" strokeLinejoin="round">
            <path d="M12 3l7 3v5c0 4.2-2.9 7.4-7 8.5-4.1-1.1-7-4.3-7-8.5V6l7-3z" />
          </svg>
        </div>
        <div className="no-sub-title">У вас пока нет подписки</div>
        <p className="no-sub-text">
          Оформите подписку RiseVPN — {deviceLimit} устройств, {lteLimit} ГБ на LTE и безлимит на остальных локациях.
        </p>
        <button onClick={startNew} className="no-sub-button">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
            <rect x="2.5" y="5.5" width="19" height="13" rx="2.5" />
            <path d="M2.5 9.5h19" />
          </svg>
          Купить подписку
        </button>
        <div className="no-sub-note">Подписка привяжется к аккаунту после входа через @risevpn_bot</div>
      </div>
    );
  }


  return (
    <div>
      {subsVM.map((sub) => (
        <div key={sub.id} style={sub.cardStyle} className={`sub-card ${sub.active ? '' : 'inactive-sub'}`}>
          <div className="subcard-grid">
            <div>
              <div className="sub-header">
                <div className="sub-header-left">
                  <div onClick={sub.toggle} style={sub.checkStyle}>
                    {sub.selected && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#06080b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12l5 5L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className={`sub-status-dot ${sub.active ? '' : 'inactive'}`}></span>
                  <div className="sub-name-wrap">
                    <span className="sub-name">{sub.name}</span>
                    {sub.keyName && (
                      <span className="sub-key-name" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', display: 'block', marginTop: '2px' }}>
                        {sub.keyName}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`sub-badge ${sub.active ? '' : 'inactive'}`}>
                  {sub.active ? 'Активна' : 'Неактивна'}
                </span>
              </div>

              <div className="stat-grid">
                <div className="stat-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7" strokeLinecap="round">
                    <circle cx="12" cy="12" r="8.5" />
                    <path d="M12 7.5V12l3 2" />
                  </svg>
                  <div>
                    <div className="stat-label">Действует до</div>
                    <div className="stat-value">{sub.until}</div>
                  </div>
                </div>
                <div className="stat-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7" strokeLinejoin="round">
                    <path d="M12 3l7 3v5c0 4.2-2.9 7.4-7 8.5-4.1-1.1-7-4.3-7-8.5V6l7-3z" />
                  </svg>
                  <div>
                    <div className="stat-label">Устройства</div>
                    <div className="stat-value">{sub.devicesUsed ?? 0} из {sub.devices}</div>
                  </div>
                </div>
                <div className="stat-item">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4aae88" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 9c4.5-4 11.5-4 16 0" />
                    <path d="M7 12.5c3-2.6 7-2.6 10 0" />
                    <path d="M10 16c1.2-1 2.8-1 4 0" />
                    <circle cx="12" cy="19" r=".8" fill="#4aae88" />
                  </svg>
                  <div>
                    <div className="stat-label">LTE · осн. безлимит</div>
                    <div className="stat-value">
                      {sub.isUnlimited
                        ? 'Безлимит'
                        : `${sub.usedGB} / ${sub.limitGB} ГБ`}
                    </div>
                  </div>
                </div>
              </div>

              <div className="sub-link-label">Ссылка на подписку:</div>
              <div className="sub-link-row">
                <div className="sub-link">{sub.link}</div>
                <button onClick={sub.copy} className="sub-copy-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="8.5" y="8.5" width="12" height="12" rx="2.5" />
                    <path d="M15.5 8.5V6a2 2 0 0 0-2-2h-7a2 2 0 0 0-2 2v7a2 2 0  Pan 0 2 2h2.5" />
                  </svg>
                  {sub.copied ? <span>Готово ✓</span> : <span>Скопировать</span>}
                </button>
                <button
                  className="sub-connect-btn"
                  onClick={() => window.open(sub.link, '_blank')}
                >
                  Подключиться
                </button>
              </div>
            </div>

            <div className="sub-qr-col">
              <div className="sub-qr-box">
                <QRCodeSVG
                  value={sub.link}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#06080b"
                  level="H"
                  includeMargin={false}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'block'
                  }}
                />
              </div>
              <div className="sub-qr-label">Скан в приложении</div>
            </div>

            <button
              className="sub-manage-btn"
              onClick={() => setExpandedSubId(expandedSubId === sub.id ? null : sub.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>⚙️</span>
                Управление подпиской
              </div>
              <span style={{
                fontSize: '18px',
                opacity: 0.7,
                transform: expandedSubId === sub.id ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.3s'
              }}>›</span>
            </button>

            {expandedSubId === sub.id && (
              <div className="manage-panel">
                <div className="manage-header">Продлить подписку</div>

                <div className="term-selector">
                  <div className="section-label">Срок:</div>
                  <div className="option-group">
                    <button
                      className={`term-btn ${selectedTerm === '30' ? 'active' : ''}`}
                      onClick={() => handleTermSelect('30')}
                    >
                      30 дней
                    </button>
                    <button
                      className={`term-btn ${selectedTerm === '90' ? 'active' : ''}`}
                      onClick={() => handleTermSelect('90')}
                    >
                      90 дней
                    </button>
                    <button
                      className={`term-btn ${selectedTerm === '180' ? 'active' : ''}`}
                      onClick={() => handleTermSelect('180')}
                    >
                      180 дней
                    </button>
                    <button
                      className={`term-btn ${selectedTerm === '365' ? 'active' : ''}`}
                      onClick={() => handleTermSelect('365')}
                    >
                      365 дней
                    </button>
                  </div>
                </div>

                <div className="traffic-selector">
                  <div className="section-label">Трафик</div>
                  <div className="option-group traffic-options">
                    {['50', '100', '150', '300', '600', '1000', '2000', '3000'].map((gb) => { 
                      const isAvailable = availableTraffic.includes(gb);
                      return (
                        <button
                          key={gb}
                          className={`traffic-btn ${selectedTraffic === gb ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                          onClick={() => isAvailable && setSelectedTraffic(gb)}
                          disabled={!isAvailable}
                          style={!isAvailable ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                        >
                          {gb} ГБ
                        </button>
                      );
                    })}
                  </div>
                  {!selectedTerm && (
                    <div style={{ color: '#c29b01', fontSize: '13px', marginTop: '8px', lineHeight: '1.4' }}>
                      Трафик действует только на активную подписку. Без продления подписка работать не будет
                    </div>
                  )}
                </div>

                <div className="price-summary">
                  {selectedTerm ? (
                    <>
                      <div className="price-row">
                        <span>
                          {selectedTerm === '30' ? '30 дней' :
                          selectedTerm === '90' ? '90 дней' :
                          selectedTerm === '180' ? '180 дней' :
                          selectedTerm === '365' ? '365 дней' : ''}
                        </span>
                        <span>{basePrice} ₽</span>
                      </div>
                      <div className="price-row" style={{ color: '#4aae88' }}>
                        <span>{selectedTraffic} ГБ</span>
                        <span>+{priceAddition} ₽</span>
                      </div>
                      <div className="price-total">
                        <span>Итого</span>
                        <span>{currentPrice} ₽</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="price-row">
                        <span>{selectedTraffic} ГБ</span>
                        <span>{trafficPrice} ₽</span>
                      </div>
                      <div className="price-total">
                        <span>Итого</span>
                        <span>{currentPrice} ₽</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="payment-methods">
                  <div className="section-label">Способ оплаты:</div>
                  <div className="option-group">
                    <button
                      className={`method-btn ${selectedPayment === 'sbp' ? 'active' : ''}`}
                      onClick={() => setSelectedPayment('sbp')}
                    >
                      СБП
                    </button>
                    <button
                      className={`method-btn ${selectedPayment === 'card' ? 'active' : ''}`}
                      onClick={() => setSelectedPayment('card')}
                    >
                      Банковская карта
                    </button>
                    <button
                      className={`method-btn ${selectedPayment === 'Crypto' ? 'active' : ''}`}
                      onClick={() => setSelectedPayment('Crypto')}
                    >
                      Криптовалюта
                    </button>
                  </div>
                </div>

                <button
                  className="pay-button"
                  onClick={() => handleRenew(sub.id)}
                  disabled={loadingPay || currentPrice === 0}
                >
                  {loadingPay ? 'Обработка...' : `Оплатить ${currentPrice} ₽`}
                </button>

                <div className="devices-block">
                  <div className="manage-header">Подключённые устройства</div>
                  <p className="reissue-desc">
                    Список устройств, на которых активна эта подписка
                  </p>
                  <button
                    className="devices-button"
                    onClick={() => openDevicesModal(sub.id)}
                  >
                    Подключённые устройства
                  </button>
                </div>

                <div className="reissue-block">
                  <div className="manage-header">Перевыпуск подписки</div>
                  <p className="reissue-desc">
                    Замена ссылки на подписку с отключением всех устройств
                  </p>
                  <button
                    className="reissue-button"
                    onClick={() => {
                      setReissueSubId(sub.id);
                      setReissueModalOpen(true);
                    }}
                  >
                    Перевыпустить
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      <div className="sub-actions">
        {anySelected ? (
          <button onClick={startRenew} className="sub-actions-renew">
            Продлить выбранные ({selectedCount})
          </button>
        ) : (
          <button
            onClick={() => navTo('payment')}
            className="sub-actions-renew"
          >
            Купить / Продлить
          </button>
        )}

        <button onClick={startNew} className="sub-actions-new">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Новая подписка
        </button>
      </div>

      {devicesModalOpen && (
        <div className="reissue-modal-overlay" onClick={closeDevicesModal}>
          <div className="reissue-modal devices-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reissue-modal-title">Подключённые устройства</div>
            <div className="reissue-modal-text">
              {loadingDevices ? (
                <p>Загрузка списка устройств…</p>
              ) : devicesList.length === 0 ? (
                <p>Нет подключённых устройств.</p>
              ) : (
                <ul className="devices-list">
                  {devicesList.map((device) => (
                    <li key={device.hwid || device.name} className="device-item">
                      <button
                        type="button"
                        className="device-delete-btn"
                        title="Удалить устройство"
                        onClick={() => requestDeleteDevice(device)}
                        disabled={loadingDeleteDevice}
                      >
                        ✕
                      </button>
                      <div className="device-info">
                        <div className="device-name">{device.name || 'Устройство'}</div>
                        <div className="device-meta">
                          <span>Подключено: {formatDeviceDate(device.created_at)}</span>
                          <span>Обновлено: {formatDeviceDate(device.updated_at)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="reissue-modal-actions">
              <button
                className="reissue-modal-btn reissue-modal-no"
                onClick={closeDevicesModal}
                disabled={loadingDeleteDevice}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmOpen && deviceToDelete && (
        <div className="reissue-modal-overlay" onClick={() => !loadingDeleteDevice && setDeleteConfirmOpen(false)}>
          <div className="reissue-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reissue-modal-title">Удалить устройство?</div>
            <div className="reissue-modal-text">
              <p>
                Устройство <strong>{deviceToDelete.name || 'Устройство'}</strong> будет отключено
                от подписки.
              </p>
            </div>
            <div className="reissue-modal-actions">
              <button
                className="reissue-modal-btn reissue-modal-yes"
                onClick={handleDeleteDevice}
                disabled={loadingDeleteDevice}
              >
                {loadingDeleteDevice ? 'Удаление…' : 'Да, удалить'}
              </button>
              <button
                className="reissue-modal-btn reissue-modal-no"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={loadingDeleteDevice}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {reissueModalOpen && (
        <div className="reissue-modal-overlay" onClick={() => !loadingReissue && setReissueModalOpen(false)}>
          <div className="reissue-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reissue-modal-title">Перевыпуск подписки</div>
            <div className="reissue-modal-text">
              <p>
                Используйте перевыпуск, если подписка попала в чужие руки или вы не уверены в количестве подключённых устройств.
              </p>
              <p style={{ marginTop: '12px', marginBottom: '8px' }}>
                <strong>Что произойдёт:</strong>
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li>Все подключённые устройства будут отключены.</li>
                <li>Вы получите новую ссылку на подписку.</li>
                <li>Оплата и срок действия сохраняются.</li>
                <li>Нужно будет заново подключиться на устройствах.</li>
              </ul>
            </div>
            <div className="reissue-modal-actions">
              <button
                className="reissue-modal-btn reissue-modal-yes"
                onClick={handleReissue}
                disabled={loadingReissue}
              >
                {loadingReissue ? 'Обработка...' : 'Да'}
              </button>
              <button
                className="reissue-modal-btn reissue-modal-no"
                onClick={() => setReissueModalOpen(false)}
                disabled={loadingReissue}
              >
                Нет
              </button>
            </div>
          </div>
        </div>
      )}
      {reissueSuccessOpen && (
        <div className="reissue-modal-overlay">
          <div className="reissue-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reissue-success-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9.5" />
                <path d="M8 12.5l2.8 2.8L16.5 9.5" />
              </svg>
            </div>
            <div className="reissue-success-text">
              Подписка перевыпущена. Все устройства отключены.
            </div>
            <button
              className="reissue-success-btn"
              onClick={() => {
                setReissueSuccessOpen(false);
                window.location.reload();
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;